import { defineStore } from "pinia";

import catalogItemsApi from "@/services/api/catalogItemsApi";

import { useNotificationStore } from "@/stores/notificationStore";

import {
  catalogItemUsageScopeOptions,
  formatCatalogItemUsageScopes,
} from "@/utils/catalogItemUsageScopeUtils";
import { getCompanyId } from "@/utils/userUtils";

export const useCatalogItemStore = defineStore("catalogItem", {
  state: () => ({
    catalogItems: [],
    catalogItemSelectItems: [],
    selectedCatalogItem: null,
    companyId: getCompanyId(),
    dataIsLoading: false,
    isSubmitting: false,
    lastFetchParams: {},
    // TODO: Получать options с backend, чтобы frontend не дублировал
    // Catalog::Item enums и Services::Vat::Types.
    baseTypeOptions: [
      { value: "service", label: "Работа" },
      { value: "part", label: "Запчасть" },
      { value: "material", label: "Материал" },
    ],
    usageScopeOptions: catalogItemUsageScopeOptions,
    vatKindOptions: [
      { value: "with_vat", label: "С НДС" },
      { value: "zero", label: "НДС 0%" },
      { value: "no_vat", label: "Без НДС" },
    ],
    vatCalculationTypeOptions: [
      { value: "included", label: "НДС включён" },
      { value: "on_top", label: "НДС сверху" },
    ],
    pagination: {
      page: 1,
      pages: 1,
      series: [],
      next: null,
      prev: null,
      count: 0,
      limit: 40,
    },
  }),

  getters: {
    baseTypeLabels: (state) =>
      Object.fromEntries(
        state.baseTypeOptions.map((option) => [
          option.value,
          option.label,
        ])
      ),

    vatKindLabels: (state) =>
      Object.fromEntries(
        state.vatKindOptions.map((option) => [
          option.value,
          option.label,
        ])
      ),

    vatCalculationTypeLabels: (state) =>
      Object.fromEntries(
        state.vatCalculationTypeOptions.map((option) => [
          option.value,
          option.label,
        ])
      ),
  },

  actions: {
    normalizeResponseData(response) {
      return response.data.result || response.data || [];
    },

    applyPagination(response) {
      const meta = response.data.pagination_meta;
      if (!meta) return;

      this.pagination = {
        page: meta.page,
        pages: meta.pages,
        series: meta.series,
        next: meta.next,
        prev: meta.prev,
        count: meta.count,
        limit: meta.limit,
      };
    },

    async fetchCatalogItems(params = {}) {
      this.dataIsLoading = true;
      this.lastFetchParams = params;

      try {
        const response = await catalogItemsApi.index(
          this.companyId,
          params
        );
        this.catalogItems = this.normalizeResponseData(response);
        this.applyPagination(response);
        return this.catalogItems;
      } finally {
        this.dataIsLoading = false;
      }
    },

    async fetchCatalogItem(id) {
      this.dataIsLoading = true;

      try {
        const response = await catalogItemsApi.show(
          this.companyId,
          id
        );
        this.selectedCatalogItem = this.normalizeResponseData(response);
        return this.selectedCatalogItem;
      } finally {
        this.dataIsLoading = false;
      }
    },

    async fetchCatalogItemSelectItems(params = {}) {
      this.dataIsLoading = true;

      try {
        const response = await catalogItemsApi.index(
          this.companyId,
          params
        );
        this.catalogItemSelectItems = this.normalizeResponseData(response);
        return this.catalogItemSelectItems;
      } finally {
        this.dataIsLoading = false;
      }
    },

    async fetchCatalogItemSelectItem(id) {
      const exists =
        this.catalogItemSelectItems.find(
          (item) => String(item.id) === String(id)
        ) ||
        this.catalogItems.find((item) => String(item.id) === String(id));
      if (exists) return exists;

      return await this.fetchCatalogItem(id);
    },

    async createCatalogItem(payload) {
      this.isSubmitting = true;

      try {
        const response = await catalogItemsApi.create(
          this.companyId,
          payload
        );
        const catalogItem = this.normalizeResponseData(response);

        useNotificationStore().showSuccess("Позиция каталога создана");
        await this.fetchCatalogItems(this.lastFetchParams);

        return catalogItem;
      } finally {
        this.isSubmitting = false;
      }
    },

    async updateCatalogItem(id, payload) {
      this.isSubmitting = true;

      try {
        const response = await catalogItemsApi.update(
          this.companyId,
          id,
          payload
        );
        const catalogItem = this.normalizeResponseData(response);

        useNotificationStore().showSuccess("Позиция каталога обновлена");
        await this.fetchCatalogItems(this.lastFetchParams);

        return catalogItem;
      } finally {
        this.isSubmitting = false;
      }
    },

    updateCatalogItemInList(catalogItem) {
      this.catalogItems = this.catalogItems.map((item) =>
        String(item.id) === String(catalogItem.id) ? catalogItem : item
      );
    },

    async archiveCatalogItem(id) {
      this.isSubmitting = true;

      try {
        const response = await catalogItemsApi.archive(this.companyId, id);
        const catalogItem = this.normalizeResponseData(response);

        this.updateCatalogItemInList(catalogItem);
        useNotificationStore().showSuccess("Позиция каталога архивирована");

        return catalogItem;
      } finally {
        this.isSubmitting = false;
      }
    },

    async restoreCatalogItem(id) {
      this.isSubmitting = true;

      try {
        const response = await catalogItemsApi.restore(this.companyId, id);
        const catalogItem = this.normalizeResponseData(response);

        this.updateCatalogItemInList(catalogItem);
        useNotificationStore().showSuccess("Позиция каталога восстановлена");

        return catalogItem;
      } finally {
        this.isSubmitting = false;
      }
    },

    formatBaseType(baseType) {
      return this.baseTypeLabels[baseType] || baseType || "—";
    },

    formatVatKind(vatKind) {
      return this.vatKindLabels[vatKind] || vatKind || "—";
    },

    formatVatCalculationType(type) {
      return this.vatCalculationTypeLabels[type] || type || "—";
    },

    formatUsageScopes(usageScopes) {
      return formatCatalogItemUsageScopes(usageScopes);
    },
  },
});
